import React, { useState, useEffect } from 'react';
import {
  QuestionMarkCircleIcon,
  ChatBubbleLeftRightIcon,
  TicketIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';

interface SupportTicket {
  id: number;
  ticket_number: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  category?: string;
  admin_response?: string;
  responded_at?: string;
  created_at: string;
}

const Support: React.FC = () => {
  const [activeSection, setActiveSection] = useState('faq');
  const [contactForm, setContactForm] = useState({
    subject: '',
    message: '',
    category: '',
    priority: 'medium'
  });
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [showTicketModal, setShowTicketModal] = useState(false);

  const faqs = [
    {
      question: 'How do I create a new order?',
      answer: 'Click on "Create New Order" from your dashboard, fill in the pickup and delivery locations, add package details, select vehicle type, and confirm the order.'
    },
    {
      question: 'How do I track my delivery?',
      answer: 'Go to "Track Orders" from the menu to see real-time tracking of all your active deliveries with rider location and ETA.'
    },
    {
      question: 'What is the OTP delivery confirmation?',
      answer: 'OTP (One-Time Password) is a 6-digit security code shown to you when your order is in transit. Share this code with your rider to confirm safe delivery.'
    },
    {
      question: 'How do I add funds to my wallet?',
      answer: 'Visit the Wallet page and click "Top Up Wallet". You can add funds via credit/debit card or bank transfer.'
    },
    {
      question: 'What vehicle types are available?',
      answer: 'We offer Bicycle, Motorbike, Sedan, SUV, Pickup Truck, Medium Van, 12-Wheeler, 18-Wheeler, Motorboat, Light Plane, Helicopter, and Cargo Plane for various delivery needs.'
    },
    {
      question: 'How is pricing calculated?',
      answer: 'Pricing is based on distance, delivery duration, and vehicle type. You can see the estimated cost before confirming your order.'
    }
  ];

  useEffect(() => {
    if (activeSection === 'tickets') {
      fetchTickets();
    }
  }, [activeSection]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/support/tickets');
      setTickets(response.data.tickets);
    } catch (err: any) {
      console.error('Error fetching tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!contactForm.subject.trim() || !contactForm.message.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/support/tickets', {
        subject: contactForm.subject,
        message: contactForm.message,
        category: contactForm.category || undefined,
        priority: contactForm.priority
      });
      
      setSuccess('Support ticket created successfully! We will respond soon.');
      setContactForm({ subject: '', message: '', category: '', priority: 'medium' });
      
      setTimeout(() => {
        setSuccess('');
        setActiveSection('tickets');
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create support ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">Support & Help</h1>
          <p className="mt-2 text-gray-600 font-poppins">Get help and answers to your questions</p>
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-volexx-lg p-4 mb-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
              <p className="text-sm font-medium text-green-800 font-poppins">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-volexx-lg p-4 mb-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm font-medium text-red-800 font-poppins">{error}</p>
            </div>
          </div>
        )}

        {/* Section Tabs */}
        <div className="mb-6 flex space-x-4 border-b">
          <button
            onClick={() => setActiveSection('faq')}
            className={`pb-3 px-4 font-semibold transition-colors font-poppins ${
              activeSection === 'faq'
                ? 'text-volexx-blue-600 border-b-2 border-volexx-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <QuestionMarkCircleIcon className="h-5 w-5 inline mr-2" />
            FAQs
          </button>
          <button
            onClick={() => setActiveSection('contact')}
            className={`pb-3 px-4 font-semibold transition-colors font-poppins ${
              activeSection === 'contact'
                ? 'text-volexx-blue-600 border-b-2 border-volexx-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <ChatBubbleLeftRightIcon className="h-5 w-5 inline mr-2" />
            Contact Us
          </button>
          <button
            onClick={() => setActiveSection('tickets')}
            className={`pb-3 px-4 font-semibold transition-colors font-poppins ${
              activeSection === 'tickets'
                ? 'text-volexx-blue-600 border-b-2 border-volexx-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <TicketIcon className="h-5 w-5 inline mr-2" />
            My Tickets
          </button>
        </div>

        {/* FAQs Section */}
        {activeSection === 'faq' && (
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start font-poppins">
                  <QuestionMarkCircleIcon className="h-6 w-6 text-volexx-blue-500 mr-2 flex-shrink-0" />
                  {faq.question}
                </h3>
                <p className="text-gray-600 ml-8 font-poppins">{faq.answer}</p>
              </div>
            ))}
          </div>
        )}

        {/* Contact Form */}
        {activeSection === 'contact' && (
          <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 font-poppins">Contact Support</h2>
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={contactForm.subject}
                  onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                  placeholder="Brief description of your issue (5-200 characters)"
                  minLength={5}
                  maxLength={200}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                    Category
                  </label>
                  <select
                    value={contactForm.category}
                    onChange={(e) => setContactForm({ ...contactForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                  >
                    <option value="">Select Category</option>
                    <option value="payment">Payment</option>
                    <option value="delivery">Delivery</option>
                    <option value="technical">Technical</option>
                    <option value="account">Account</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                    Priority
                  </label>
                  <select
                    value={contactForm.priority}
                    onChange={(e) => setContactForm({ ...contactForm, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                  Message <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:ring-2 focus:ring-volexx-blue-500 focus:border-volexx-blue-500 font-poppins"
                  placeholder="Describe your issue in detail (20-2000 characters)"
                  minLength={20}
                  maxLength={2000}
                  required
                />
                <p className="mt-1 text-xs text-gray-500 font-poppins">{contactForm.message.length}/2000 characters</p>
              </div>

              <button 
                type="submit" 
                className="btn-primary w-full font-poppins disabled:opacity-50" 
                disabled={submitting}
              >
                {submitting ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  'Submit Ticket'
                )}
              </button>
            </form>

            <div className="mt-8 p-4 bg-volexx-blue-50 rounded-volexx border border-volexx-blue-200">
              <h3 className="font-semibold text-volexx-blue-900 mb-2 font-poppins">Need immediate help?</h3>
              <p className="text-sm text-volexx-blue-700 font-poppins">
                Our support team typically responds within 24 hours. For urgent matters, you can also reach us at:
              </p>
              <p className="text-sm font-semibold text-volexx-blue-900 mt-2 font-poppins">
                Email: support@volexx.com | Phone: +1 (555) 123-4567
              </p>
            </div>
          </div>
        )}

        {/* Support Tickets */}
        {activeSection === 'tickets' && (
          <div className="bg-white rounded-volexx-lg shadow-volexx-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 font-poppins">My Support Tickets</h2>
              <button
                onClick={fetchTickets}
                className="px-4 py-2 bg-volexx-blue-600 text-white rounded-volexx hover:bg-volexx-blue-700 transition-colors font-poppins font-semibold text-sm"
              >
                Refresh
              </button>
            </div>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-volexx-blue-600"></div>
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12">
                <TicketIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 font-poppins">No support tickets yet</p>
                <button
                  onClick={() => setActiveSection('contact')}
                  className="mt-4 btn-primary font-poppins"
                >
                  Create New Ticket
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((ticket) => (
                  <div key={ticket.id} className="border border-gray-200 rounded-volexx p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900 font-poppins">{ticket.ticket_number}</span>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-volexx font-poppins capitalize ${getPriorityBadgeColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                        <span className={`px-2 py-1 text-xs font-semibold rounded-volexx font-poppins capitalize ${getStatusBadgeColor(ticket.status)}`}>
                          {ticket.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 font-poppins">{ticket.subject}</h3>
                    <p className="text-xs text-gray-600 mb-2 font-poppins line-clamp-2">{ticket.message}</p>
                    {ticket.category && (
                      <span className="text-xs text-gray-500 font-poppins capitalize">Category: {ticket.category}</span>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-gray-500 font-poppins">
                        Created: {new Date(ticket.created_at).toLocaleString()}
                      </p>
                      <button
                        onClick={() => {
                          setSelectedTicket(ticket);
                          setShowTicketModal(true);
                        }}
                        className="text-sm text-volexx-blue-600 hover:text-volexx-blue-800 font-semibold font-poppins"
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Ticket Details Modal */}
        {showTicketModal && selectedTicket && (
          <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowTicketModal(false)}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 font-poppins">
                      Ticket: {selectedTicket.ticket_number}
                    </h3>
                    <button
                      onClick={() => setShowTicketModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-500 font-poppins">Status</p>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-volexx font-poppins capitalize ${getStatusBadgeColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace('_', ' ')}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-poppins">Priority</p>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-volexx font-poppins capitalize ${getPriorityBadgeColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-poppins">Category</p>
                      <p className="text-sm font-semibold text-gray-900 font-poppins capitalize">{selectedTicket.category || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-poppins">Created</p>
                      <p className="text-sm font-semibold text-gray-900 font-poppins">
                        {new Date(selectedTicket.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">Subject</label>
                    <p className="text-sm text-gray-900 font-poppins">{selectedTicket.subject}</p>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">Your Message</label>
                    <div className="bg-gray-50 rounded-volexx p-4">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap font-poppins">{selectedTicket.message}</p>
                    </div>
                  </div>

                  {selectedTicket.admin_response && (
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">Admin Response</label>
                      <div className="bg-green-50 rounded-volexx p-4 border border-green-200">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap font-poppins">{selectedTicket.admin_response}</p>
                        {selectedTicket.responded_at && (
                          <p className="text-xs text-gray-500 mt-2 font-poppins">
                            Response sent on {new Date(selectedTicket.responded_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={() => setShowTicketModal(false)}
                      className="px-6 py-2 bg-gray-200 text-gray-700 rounded-volexx hover:bg-gray-300 transition-colors font-poppins font-semibold"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Support;
